import React, { useRef, useState } from "react";
import { useSpring } from "react-spring";
import { useGesture } from "react-use-gesture";
import {
  PipContainer,
  PipHeader,
  PipGrip,
  PipActions,
  PipButton,
  PipImg,
} from "./styled";

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

// Base window width at scale = 1; zoom drives the window width off this.
const BASE_WIDTH = 520;

// Zoom bounds. The upper bound is refined per image once it loads: the window
// height is header + image (the image keeps its natural ratio at width:100%),
// so zooming in far enough would make it taller than the screen. See
// maxScaleFor().
const MIN_SCALE = 0.5;
const MAX_SCALE = 3;

// Header bar height: 30px buttons + 7px padding top and bottom. Fixed — it does
// not grow with zoom, so it is a constant part of the window height.
const HEADER_H = 44;

// The window lives on the 1920x1080 stage canvas; keep it a bit shorter than
// the full height so it never runs edge to edge.
const MAX_WINDOW_H = 1040;

// Largest zoom that still fits the window in the stage height, for an image of
// the given natural ratio (height / width). Never above MAX_SCALE, never below
// MIN_SCALE (a very tall image just gets its own smaller ceiling).
function maxScaleFor(ratio) {
  if (!ratio || !isFinite(ratio)) return MAX_SCALE;

  return clamp((MAX_WINDOW_H - HEADER_H) / (BASE_WIDTH * ratio), MIN_SCALE, MAX_SCALE);
}

// The window's position (x/y) and size (scale) persist in localStorage, so the
// spot the client drags it to survives reloads and other empreendimentos — the
// kiosk is placed once and should stay put. Same key prefix / defensive
// read-write as the theme cache in ConfigContext.
const PIP_STATE_KEY = "magicFolioPipWindow";

function readPipState() {
  try {
    const s = JSON.parse(localStorage.getItem(PIP_STATE_KEY));
    if (!s) return null;
    const x = Number(s.x);
    const y = Number(s.y);
    const scale = Number(s.scale);
    if (!isFinite(x) || !isFinite(y) || !isFinite(scale)) return null;

    // Sanity clamp only; the per-image ceiling is applied once the image loads.
    return { x, y, scale: clamp(scale, MIN_SCALE, MAX_SCALE) };
  } catch (e) {
    return null;
  }
}

function writePipState(state) {
  try {
    localStorage.setItem(PIP_STATE_KEY, JSON.stringify(state));
  } catch (e) {
    /* ignore quota / disabled storage */
  }
}

// Magnifier-plus / magnifier-minus glyphs so zoom reads distinctly from the
// minimize dash (they used to be "+" / "−", the latter easy to confuse with "—").
const ZoomInIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.6" />
    <line x1="11" y1="11" x2="14.5" y2="14.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <line x1="7" y1="5" x2="7" y2="9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <line x1="5" y1="7" x2="9" y2="7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const ZoomOutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.6" />
    <line x1="11" y1="11" x2="14.5" y2="14.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <line x1="5" y1="7" x2="9" y2="7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

// Floating, touch-draggable "map" window shown over the maximized planta. The
// admin uploads the image (type = pip); the client drags this window around the
// screen (and pinch-zooms it) to use it as a reference map while the plantas
// stay visible underneath. The box is sized to the image (header overlays it),
// so no window background is ever shown.
const PipWindow = ({ pipImage }) => {
  const domTarget = useRef(null);
  const [collapsed, setCollapsed] = useState(false);

  // Restored position/size from the last session (null when never moved).
  const saved = useRef(readPipState()).current;

  // x/y = window position (drag), scale = zoom (pinch / buttons). scale drives
  // the window WIDTH (BASE_WIDTH * scale); the image is width:100% so its height
  // — and thus the window height — follows. The header keeps its fixed height
  // (padding), so it only ever changes width with the window.
  const [{ x, y, scale }, set] = useSpring(() => ({
    x: saved ? saved.x : 0,
    y: saved ? saved.y : 0,
    scale: saved ? saved.scale : 1,
    config: { mass: 1, tension: 350, friction: 40 },
  }));

  // Latest committed position/size, updated on every gesture frame. The end
  // handlers persist THIS rather than x.get()/scale.get(): when a gesture ends
  // the spring is still settling toward the target, so reading it there can
  // store a mid-animation value.
  const latest = useRef({
    x: saved ? saved.x : 0,
    y: saved ? saved.y : 0,
    scale: saved ? saved.scale : 1,
  });

  // Per-image zoom ceiling, computed from the image's natural ratio on load.
  // MAX_SCALE until then.
  const maxScaleRef = useRef(MAX_SCALE);

  // Once the map image loads, its ratio tells us how far it can be zoomed before
  // the window is taller than the screen. If a restored zoom is already past
  // that ceiling, pull it back in (and persist the corrected value).
  const handleImgLoad = (e) => {
    const img = e.currentTarget;
    if (!img.naturalWidth || !img.naturalHeight) return;

    const max = maxScaleFor(img.naturalHeight / img.naturalWidth);
    maxScaleRef.current = max;

    if (latest.current.scale > max) {
      latest.current = { ...latest.current, scale: max };
      set({ scale: max });
      writePipState(latest.current);
    }
  };

  useGesture(
    {
      // Drag anywhere on the window to reposition it. Base each drag on the
      // spring's CURRENT position (captured in memo at drag start) plus this
      // gesture's movement — not react-use-gesture's cumulative `offset`, which
      // wouldn't know about programmatic changes like reset() and would jump.
      // `immediate: down` makes the window track the finger 1:1 while touching
      // (no spring lag / dead zone); on release the spring settles.
      onDrag: ({ down, movement: [mx, my], memo = [x.get(), y.get()] }) => {
        const nextX = memo[0] + mx;
        const nextY = memo[1] + my;
        latest.current = { ...latest.current, x: nextX, y: nextY };
        set({ x: nextX, y: nextY, immediate: down });
        return memo;
      },
      // Persist once the finger lifts, not on every frame.
      onDragEnd: () => writePipState(latest.current),
      // Pinch to zoom — resizes the window (header height excepted). Capped so
      // the window never grows taller than the screen.
      onPinch: ({ da: [d], memo }) => {
        if (!memo) memo = { d0: d || 1, s0: scale.get() };
        const nextScale = clamp(
          (memo.s0 * d) / memo.d0,
          MIN_SCALE,
          maxScaleRef.current
        );
        latest.current = { ...latest.current, scale: nextScale };
        set({ scale: nextScale });
        return memo;
      },
      onPinchEnd: () => writePipState(latest.current),
    },
    { domTarget, eventOptions: { passive: false } }
  );

  // Keep button taps from bubbling into the gesture target.
  const stop = (e) => e.stopPropagation();
  const zoom = (factor) => {
    const next = clamp(
      latest.current.scale * factor,
      MIN_SCALE,
      maxScaleRef.current
    );
    latest.current = { ...latest.current, scale: next };
    set({ scale: next });
    writePipState(latest.current);
  };
  const reset = () => {
    latest.current = { x: 0, y: 0, scale: 1 };
    set({ x: 0, y: 0, scale: 1 });
    writePipState(latest.current);
  };

  // NOTE: no `if (!pipImage) return null` here — the useGesture domTarget must
  // stay mounted, so the parent only renders PipWindow once pipImage exists.
  return (
    <PipContainer
      ref={domTarget}
      className={collapsed ? "collapsed" : ""}
      style={{ x, y, width: scale.to((s) => BASE_WIDTH * s) }}
    >
      <PipHeader className="pip-header">
        <PipGrip />
        <PipActions>
          <PipButton
            onPointerDown={stop}
            onClick={(e) => {
              stop(e);
              zoom(1.25);
            }}
            title="Aumentar"
          >
            <ZoomInIcon />
          </PipButton>
          <PipButton
            onPointerDown={stop}
            onClick={(e) => {
              stop(e);
              zoom(0.8);
            }}
            title="Diminuir"
          >
            <ZoomOutIcon />
          </PipButton>
          <PipButton
            onPointerDown={stop}
            onClick={(e) => {
              stop(e);
              reset();
            }}
            title="Centralizar"
          >
            ⟳
          </PipButton>
          <PipButton
            onPointerDown={stop}
            onClick={(e) => {
              stop(e);
              setCollapsed((c) => !c);
            }}
            title={collapsed ? "Expandir" : "Recolher"}
          >
            {collapsed ? "▢" : "—"}
          </PipButton>
        </PipActions>
      </PipHeader>
      <PipImg
        className="pip-img"
        src={pipImage}
        alt="Mapa"
        draggable={false}
        onLoad={handleImgLoad}
      />
    </PipContainer>
  );
};

export default PipWindow;
